import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, TrendingUp, Target, Zap } from "lucide-react";
import { useCurrentUser } from "@/lib/api";

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Access was denied. Please try again.",
  unauthorized_domain: "Your account is not authorised. Only Applova email addresses are allowed.",
  invalid_state: "Login session expired. Please try again.",
  token_failed: "Could not complete sign-in. Please try again.",
  userinfo_failed: "Could not retrieve account info. Please try again.",
};

const FEATURES = [
  { icon: BarChart3, label: "MQL → SQL → SQO → Win funnel tracking" },
  { icon: TrendingUp, label: "Meta & Google Ads efficiency reporting" },
  { icon: Target, label: "Goal pacing with AI forecasting" },
];

export function Login() {
  const [params] = useSearchParams();
  const error = params.get("error");
  const { data: user, isLoading } = useCurrentUser();

  useEffect(() => {
    if (!isLoading && user) {
      window.location.href = "/";
    }
  }, [user, isLoading]);

  return (
    <div className="min-h-screen flex">
      {/* Left panel — dark brand panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] shrink-0 bg-[#0c0e16] flex-col px-12 py-14">
        <div>
          <div className="font-display text-2xl font-semibold text-[#e9ecf1] tracking-tight">
            Growth Insights
          </div>
          <div className="text-sm text-[#7c8597] mt-1">Marketing Intelligence Dashboard</div>
        </div>

        <div className="flex-1 flex flex-col justify-center gap-10 mt-16">
          <div>
            <p className="text-[#e9ecf1] text-3xl font-display font-semibold leading-snug">
              Your marketing funnel,<br />
              <span className="text-accent-blue">clearly understood.</span>
            </p>
            <p className="text-[#7c8597] text-sm mt-4 leading-relaxed max-w-sm">
              Real-time lead attribution, channel efficiency, and AI-powered insights — all in one place.
            </p>
          </div>

          <div className="space-y-5">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-white/5 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-accent-blue" strokeWidth={1.75} />
                </div>
                <span className="text-sm text-[#7c8597]">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-[#7c8597] mt-12">
          © {new Date().getFullYear()} Applova Inc.
        </div>
      </div>

      {/* Right panel — full-height login area */}
      <div className="flex-1 bg-canvas flex flex-col relative overflow-hidden">
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(14,20,37,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(14,20,37,0.035) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-accent-blue via-accent-cyan to-accent-teal shrink-0" />

        {/* Mobile logo */}
        <div className="lg:hidden text-center pt-10 px-6 relative z-10">
          <div className="font-display text-2xl font-semibold text-ink-900">Growth Insights</div>
          <div className="text-sm text-slate-500 mt-1">Marketing Intelligence</div>
        </div>

        {/* Centered form */}
        <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
          <div className="w-full max-w-[420px]">

            {/* Card */}
            <div className="bg-card rounded-card border border-border shadow-[0_4px_24px_rgba(15,23,42,0.08)] px-10 py-10">

              {/* Icon mark */}
              <div className="mb-6 h-11 w-11 rounded-xl bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center">
                <Zap className="h-5 w-5 text-accent-blue" strokeWidth={2} />
              </div>

              <h1 className="font-display text-2xl font-semibold text-ink-900 leading-tight">
                Welcome to Growth Insights
              </h1>
              <p className="text-sm text-slate-500 mt-2 mb-8 leading-relaxed">
                Sign in with your Applova Google account to access the Growth Insights dashboard.
              </p>

              {error && (
                <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {ERROR_MESSAGES[error] ?? "Something went wrong. Please try again."}
                </div>
              )}

              <a
                href="/auth/login"
                className="flex items-center justify-center gap-3 w-full rounded-lg border border-border bg-white px-4 py-3 text-sm font-medium text-ink-900 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-card"
              >
                <GoogleIcon />
                Continue with Google
              </a>

            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
